import { useState, useEffect, useRef } from 'react';
import { useAuth, useAuthActions } from '../state/AuthContext';
import { getInviteInfo, getAvatarUrl, checkUsername } from '../api/authClient';
import { DEFAULT_MUSIC_SERVICE } from '../utils/exportUtils';
import MusicServicePicker from './auth/MusicServicePicker';
import '../styles/invite.css';

const USERNAME_RE = /^[a-z][a-z0-9_-]{2,19}$/;

function InvitePage({ inviteCode }) {
  const { user } = useAuth();
  const { register } = useAuthActions();
  const [inviteData, setInviteData] = useState(null);
  const [inviteError, setInviteError] = useState('');
  const [loading, setLoading] = useState(true);

  // Register form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [musicService, setMusicService] = useState(DEFAULT_MUSIC_SERVICE);
  const [usernameStatus, setUsernameStatus] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);
  const usernameTimerRef = useRef(null);

  useEffect(() => {
    getInviteInfo(inviteCode)
      .then((data) => {
        setInviteData(data);
        setLoading(false);
      })
      .catch((err) => {
        setInviteError(err.message);
        setLoading(false);
      });
  }, [inviteCode]);

  useEffect(() => {
    if (!username) {
      setUsernameStatus('');
      return;
    }
    const val = username.toLowerCase();
    if (!USERNAME_RE.test(val)) {
      setUsernameStatus('invalid');
      return;
    }
    setUsernameStatus('checking');
    clearTimeout(usernameTimerRef.current);
    usernameTimerRef.current = setTimeout(async () => {
      try {
        const data = await checkUsername(val);
        setUsernameStatus(data.available ? 'available' : 'taken');
      } catch {
        setUsernameStatus('error');
      }
    }, 400);
    return () => clearTimeout(usernameTimerRef.current);
  }, [username]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    try {
      await register(email, password, displayName, username.toLowerCase(), inviteCode, musicService);
      setRegistered(true);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // User already signed in
  if (user && !registered) {
    return (
      <div className="invite-page">
        <div className="invite-page-card">
          <h1 className="invite-page-title">You're already signed in</h1>
          <p className="invite-page-desc">
            This invite link is for new users. You're already signed in as <strong>{user.displayName}</strong>.
          </p>
          <a href="/" className="invite-page-home-btn">Go to Stellar</a>
        </div>
      </div>
    );
  }

  // Just registered successfully
  if (registered) {
    return (
      <div className="invite-page">
        <div className="invite-page-card">
          <div className="invite-page-success-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="32" height="32">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h1 className="invite-page-title">Welcome to Stellar!</h1>
          <p className="invite-page-desc">
            Your account is set up and you're now friends with <strong>{inviteData?.inviter?.displayName}</strong>.
          </p>
          <a href="/" className="invite-page-home-btn">Start Exploring</a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="invite-page">
        <div className="invite-page-card">
          <div className="invite-page-loading">Loading invite...</div>
        </div>
      </div>
    );
  }

  if (inviteError) {
    return (
      <div className="invite-page">
        <div className="invite-page-card">
          <h1 className="invite-page-title">Invalid Invite</h1>
          <p className="invite-page-desc">{inviteError}</p>
          <a href="/" className="invite-page-home-btn">Go to Stellar</a>
        </div>
      </div>
    );
  }

  return (
    <div className="invite-page">
      <div className="invite-page-card">
        <div className="invite-page-inviter">
          <div className="invite-page-inviter-avatar">
            {inviteData.inviter.hasAvatar ? (
              <img src={getAvatarUrl(inviteData.inviter.id)} alt="" className="invite-page-avatar-img" />
            ) : (
              inviteData.inviter.displayName?.charAt(0) || '?'
            )}
          </div>
          <p className="invite-page-inviter-text">
            <strong>{inviteData.inviter.displayName}</strong> invited you to join Stellar
          </p>
        </div>

        <h1 className="invite-page-title">Create your account</h1>

        {formError && <div className="invite-form-error">{formError}</div>}

        <form onSubmit={handleSubmit} className="invite-form">
          <div className="invite-form-field">
            <label className="invite-form-label">Display name</label>
            <input
              className="invite-form-input"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="How you'll appear to others"
              maxLength={50}
              required
            />
          </div>

          <div className="invite-form-field">
            <label className="invite-form-label">Username</label>
            <input
              className="invite-form-input"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
              placeholder="your-unique-username"
              maxLength={20}
              required
            />
            {usernameStatus === 'checking' && <span className="invite-form-hint">Checking...</span>}
            {usernameStatus === 'available' && <span className="invite-form-hint available">Available</span>}
            {usernameStatus === 'taken' && <span className="invite-form-hint taken">Taken</span>}
            {usernameStatus === 'invalid' && username.length > 0 && (
              <span className="invite-form-hint taken">3-20 chars, starts with letter</span>
            )}
          </div>

          <div className="invite-form-field">
            <label className="invite-form-label">Email</label>
            <input
              className="invite-form-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
            />
          </div>

          <div className="invite-form-field">
            <label className="invite-form-label">Password</label>
            <input
              className="invite-form-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="8+ characters"
              minLength={8}
              required
            />
          </div>

          <div className="invite-form-field">
            <label className="invite-form-label">Open song links in</label>
            <MusicServicePicker value={musicService} onChange={setMusicService} />
            <span className="invite-form-hint">You can change this anytime in Settings.</span>
          </div>

          <button
            className="invite-form-submit"
            type="submit"
            disabled={submitting || usernameStatus === 'taken' || usernameStatus === 'invalid'}
          >
            {submitting ? 'Creating account...' : 'Join Stellar'}
          </button>
        </form>

        <p className="invite-page-login-link">
          Already have an account? <a href="/">Sign in</a>
        </p>
      </div>
    </div>
  );
}

export default InvitePage;
